<!--
Navigator Component - Hierarchical project navigation
Displays a collapsible tree view of project structure (dashboards, sites, zones)
Positioned as a left side panel that can be expanded and collapsed
-->

<template>
    <div 
        class="navigator-side-panel" 
        v-if="isVisible && dataLoaded"
        :class="{ 'collapsed': isCollapsed }"
        @click.stop
    >
        <!-- Toggle Button -->
        <div class="navigator-toggle" @click="togglePanel">
            <v-icon>{{ isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
        </div>
        
        <!-- Panel Content -->
        <div class="navigator-content" v-show="!isCollapsed">
            <!-- Project Root Node -->
            <div 
                class="tree-node project-node"
                :class="{ 'expanded': expanded.project }"
                role="button"
                :aria-expanded="expanded.project ? 'true' : 'false'"
                @click="hasProjectChildren && toggleNode('project')"
            >
                <v-icon 
                    v-if="hasProjectChildren"
                    class="tree-caret"
                    :class="{ 'expanded': expanded.project }"
                    @click.stop="toggleNode('project')"
                    small
                >
                    mdi-chevron-right
                </v-icon>
                <span 
                    class="tree-label"
                >
                    {{ currentProject.name || 'Project' }}
                </span>
                <div class="node-actions">
                    <v-icon small class="action-icon" :title="'Open project home'" @click.stop="navigateToHome">mdi-open-in-new</v-icon>
                </div>
            </div>
            
            <!-- Project Children -->
            <transition name="expand">
                <div v-if="expanded.project" class="tree-children">
                    <!-- Top-level Dashboards Section -->
                    <div 
                        v-if="topLevelDashboards.length > 0"
                        class="tree-node section-node"
                        :class="{ 'expanded': expanded.topDashboards }"
                        role="button"
                        :aria-expanded="expanded.topDashboards ? 'true' : 'false'"
                        @click="toggleNode('topDashboards')"
                    >
                        <v-icon 
                            class="tree-caret"
                            :class="{ 'expanded': expanded.topDashboards }"
                            @click.stop="toggleNode('topDashboards')"
                            small
                        >
                            mdi-chevron-right
                        </v-icon>
                        <span 
                            class="tree-label"
                        >
                            Dashboards
                        </span>
                        <div class="node-actions">
                            <v-icon small class="action-icon" :title="'Open Dashboards'" @click.stop="navigateToDashboardsPage">mdi-open-in-new</v-icon>
                        </div>
                    </div>
                    
                    <!-- Top-level Dashboards List -->
                    <transition name="expand">
                        <div v-if="expanded.topDashboards" class="tree-children">
                            <div 
                                v-for="dashboard in topLevelDashboards" 
                                :key="dashboard.objectId"
                                class="tree-node dashboard-node"
                            >
                                <span class="left-action">
                                    <v-icon small class="entity-icon">mdi-view-dashboard</v-icon>
                                    <v-icon small class="open-icon" :title="'Open dashboard'" @click.stop="navigateToDashboard(dashboard.objectId)">mdi-open-in-new</v-icon>
                                </span>
                                <span 
                                    class="tree-label"
                                    :class="{ 'active': isCurrentDashboard(dashboard.objectId) }"
                                >
                                    {{ dashboard.name }}
                                </span>
                            </div>
                        </div>
                    </transition>
                    
                    <!-- Sites Section -->
                    <div 
                        class="tree-node section-node"
                        :class="{ 'expanded': expanded.sites }"
                        role="button"
                        :aria-expanded="expanded.sites ? 'true' : 'false'"
                        @click="toggleNode('sites')"
                    >
                        <v-icon 
                            class="tree-caret"
                            :class="{ 'expanded': expanded.sites }"
                            @click.stop="toggleNode('sites')"
                            small
                        >
                            mdi-chevron-right
                        </v-icon>
                        <span class="left-spacer"></span>
                        <span 
                            class="tree-label"
                        >
                            Sites
                        </span>
                        <div class="node-actions">
                            <v-icon small class="action-icon" :title="'Open project home'" @click.stop="navigateToHome">mdi-open-in-new</v-icon>
                        </div>
                    </div>
                    
                    <!-- Sites List -->
                    <transition name="expand">
                        <div v-if="expanded.sites" class="tree-children">
                            <div 
                                v-for="site in sites" 
                                :key="site.objectId"
                            >
                                <!-- Site Node -->
                                <div
                                    class="tree-node site-node"
                                    :class="{ 'expanded': expanded[`site-${site.objectId}`] }"
                                    role="button"
                                    :aria-expanded="expanded[`site-${site.objectId}`] ? 'true' : 'false'"
                                    @click="toggleSite(site.objectId)"
                                >
                                    <v-icon 
                                        class="tree-caret"
                                        :class="{ 'expanded': expanded[`site-${site.objectId}`] }"
                                        @click.stop="toggleSite(site.objectId)"
                                        small
                                    >
                                        mdi-chevron-right
                                    </v-icon>
                                    <span class="left-action">
                                        <v-icon small class="entity-icon">mdi-office-building</v-icon>
                                    </span>
                                    <span 
                                        class="tree-label"
                                        :class="{ 'active': isCurrentSite(site.objectId) }"
                                        >
                                        {{ site.name }}
                                    </span>
                                    <div class="node-actions">
                                        <v-icon small class="action-icon" :title="'Open site'" @click.stop="navigateToSite(site.objectId)">mdi-open-in-new</v-icon>
                                    </div>
                                </div>
                                
                                <!-- Site Children (Dashboards and Zones) -->
                                <transition name="expand">
                                    <div 
                                        v-if="expanded[`site-${site.objectId}`]" 
                                        class="tree-children"
                                    >
                                        <!-- Site Dashboards -->
                                        <div 
                                            v-if="getSiteDashboards(site.objectId).length > 0"
                                            class="tree-node site-dashboards-section"
                                            :class="{ 'expanded': expanded[`siteDashboards-${site.objectId}`] }"
                                            role="button"
                                            :aria-expanded="expanded[`siteDashboards-${site.objectId}`] ? 'true' : 'false'"
                                        >
                                            <v-icon 
                                                class="tree-caret"
                                                :class="{ 'expanded': expanded[`siteDashboards-${site.objectId}`] }"
                                                @click.stop="toggleNode(`siteDashboards-${site.objectId}`)"
                                                small
                                            >
                                                mdi-chevron-right
                                            </v-icon>
                                            <span class="left-action"><v-icon small class="entity-icon">mdi-view-dashboard</v-icon></span>
                                            <span 
                                                class="tree-label"
                                            >
                                                Dashboards
                                            </span>
                                            <div class="node-actions">
                                                <v-icon small class="action-icon" :title="'Open Dashboards'" @click.stop="navigateToDashboardsPage">mdi-open-in-new</v-icon>
                                            </div>
                                        </div>
                                        
                                        <!-- Site Dashboards List -->
                                        <transition name="expand">
                                            <div v-if="expanded[`siteDashboards-${site.objectId}`]" class="tree-children">
                                                <div 
                                                    v-for="dashboard in getSiteDashboards(site.objectId)" 
                                                    :key="dashboard.objectId"
                                                    class="tree-node dashboard-node site-dashboard"
                                                >
                                                    <span class="tree-spacer"></span>
                                                    <v-icon 
                                                        class="tree-icon"
                                                        small
                                                    >
                                                        mdi-view-dashboard
                                                    </v-icon>
                                                    <span 
                                                        class="tree-label"
                                                        :class="{ 'active': isCurrentDashboard(dashboard.objectId) }"
                                                    >
                                                        {{ dashboard.name }}
                                                    </span>
                                                    <div class="node-actions">
                                                        <v-icon small class="action-icon" :title="'Open dashboard'" @click.stop="navigateToDashboard(dashboard.objectId)">mdi-open-in-new</v-icon>
                                                    </div>
                                                </div>
                                            </div>
                                        </transition>
                                        
                                        <!-- Zones Section -->
                                        <div 
                                            class="tree-node zone-section-node"
                                            :class="{ 'expanded': expanded[`siteZones-${site.objectId}`] }"
                                            role="button"
                                            :aria-expanded="expanded[`siteZones-${site.objectId}`] ? 'true' : 'false'"
                                            @click="toggleNode(`siteZones-${site.objectId}`)"
                                        >
                                            <v-icon 
                                                class="tree-caret"
                                                :class="{ 'expanded': expanded[`siteZones-${site.objectId}`] }"
                                                @click.stop="toggleNode(`siteZones-${site.objectId}`)"
                                                small
                                            >
                                                mdi-chevron-right
                                            </v-icon>
                                            <span class="left-action"><v-icon small class="entity-icon">mdi-door-open</v-icon></span>
                                            <span 
                                                class="tree-label"
                                            >
                                                Zones
                                            </span>
                                            <div class="node-actions">
                                                <v-icon small class="action-icon" :title="'Open site'" @click.stop="navigateToSite(site.objectId)">mdi-open-in-new</v-icon>
                                            </div>
                                        </div>
                                        
                                        <!-- Zones List -->
                                        <transition name="expand">
                                            <div v-if="expanded[`siteZones-${site.objectId}`]" class="tree-children ml-6">
                                                <div 
                                                    v-if="loadingZones[site.objectId]"
                                                    class="tree-node zone-node loading"
                                                >
                                                    <v-icon small class="loading-icon">mdi-loading</v-icon>
                                                    <span class="tree-label">Loading zones...</span>
                                                </div>
                                                
                                                <div 
                                                    v-else
                                                    v-for="zone in getZonesForSite(site.objectId)" 
                                                    :key="zone.objectId"
                                                >
                                                    <!-- Zone Node -->
                                                    <div
                                                        class="tree-node zone-node"
                                                        :class="{ 'expanded': expanded[`zone-${zone.objectId}`] }"
                                                        role="button"
                                                        :aria-expanded="expanded[`zone-${zone.objectId}`] ? 'true' : 'false'"
                                                        @click="getZoneDashboards(site.objectId, zone.objectId).length > 0 && toggleZone(zone.objectId)"
                                                    >
                                                        <v-icon 
                                                            v-if="getZoneDashboards(site.objectId, zone.objectId).length > 0"
                                                            class="tree-caret"
                                                            :class="{ 'expanded': expanded[`zone-${zone.objectId}`] }"
                                                            @click.stop="toggleZone(zone.objectId)"
                                                            small
                                                        >
                                                            mdi-chevron-right
                                                        </v-icon>
                                                        <span class="left-action"><v-icon small class="entity-icon">mdi-door-open</v-icon></span>
                                                        <!-- <v-icon 
                                                            v-if="getZoneDashboards(site.objectId, zone.objectId).length > 0"
                                                            class="tree-folder"
                                                            :class="{ 'expanded': expanded[`zone-${zone.objectId}`] }"
                                                            @click.stop="toggleZone(zone.objectId)"
                                                            small
                                                        >
                                                            {{ expanded[`zone-${zone.objectId}`] ? 'mdi-folder-open' : 'mdi-folder' }}
                                                        </v-icon> -->
                                                        <span 
                                                            class="tree-label"
                                                            :class="{ 'active': isCurrentZone(site.objectId, zone.objectId) }"
                                                        >
                                                            {{ zone.name }}
                                                        </span>
                                                        <div class="node-actions">
                                                            <v-icon small class="action-icon" :title="'Open zone'" @click.stop="navigateToZone(site.objectId, zone.objectId)">mdi-open-in-new</v-icon>
                                                        </div>
                                                    </div>
                                                    
                                                    <!-- Zone Dashboards -->
                                                    <transition name="expand">
                                                        <div 
                                                            v-if="expanded[`zone-${zone.objectId}`] && getZoneDashboards(site.objectId, zone.objectId).length > 0" 
                                                            class="tree-children"
                                                        >
                                                            <div 
                                                                v-for="dashboard in getZoneDashboards(site.objectId, zone.objectId)" 
                                                                :key="dashboard.objectId"
                                                                class="tree-node dashboard-node zone-dashboard"
                                                            >
                                                                <!-- <span class="tree-spacer"></span> -->
                                                                <span class="left-action"><v-icon small class="entity-icon">mdi-view-dashboard</v-icon></span>
                                                                <span 
                                                                    class="tree-label clickable"
                                                                    @click="navigateToDashboard(dashboard.objectId)"
                                                                    :class="{ 'active': isCurrentDashboard(dashboard.objectId) }"
                                                                >
                                                                    {{ dashboard.name }}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </transition>
                                                </div>
                                            </div>
                                        </transition>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </transition>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Navigator',
    
    data() {
        return {
            expanded: {
                project: true,
                sites: true,
                topDashboards: false
            },
            loadingZones: {},
            currentPath: '',
            isCollapsed: this.getStoredCollapseState()
        }
    },
    
    computed: {
        isLoggedIn() {
            return this.$store.getters.isLoggedIn;
        },
        isVisible() {
            // Only show navigator when user is authenticated and no modal is open
            return this.$store.state.navigatorVisible && !this.$store.state.modalOpen && this.isLoggedIn;
        },
        
        currentProject() {
            return this.$store.state.project || {};
        },
        
        sites() {
            console.log("sites", this.$store.state.sites);
            return this.$store.state.sites || [];
        },
        
        dashboards() {
            return this.$store.state.dashboards || [];
        },
        
        dataLoaded() {
            return !!(this.currentProject.objectId && this.sites.length > 0 && this.dashboards.length >= 0);
        },
        
        // Check if project has any children (top-level dashboards or sites)
        hasProjectChildren() {
            return this.topLevelDashboards.length > 0 || this.sites.length > 0;
        },
        
        // Get dashboards that belong to no site or zone (top-level)
        topLevelDashboards() {
            return this.dashboards.filter(dashboard => {
                const siteId = dashboard.site?.objectId || dashboard.site;
                const zoneId = dashboard.zone?.objectId || dashboard.zone;
                return !siteId && !zoneId;
            });
        },
        
        // Get dashboards that belong to a specific site but no zone
        getSiteDashboards() {
            return (siteId) => {
                return this.dashboards.filter(dashboard => {
                    const dashboardSiteId = dashboard.site?.objectId || dashboard.site;
                    const zoneId = dashboard.zone?.objectId || dashboard.zone;
                    return dashboardSiteId === siteId && !zoneId;
                });
            };
        },
        
        // Get dashboards that belong to a specific zone
        getZoneDashboards() {
            return (siteId, zoneId) => {
                return this.dashboards.filter(dashboard => {
                    const dashboardSiteId = dashboard.site?.objectId || dashboard.site;
                    const dashboardZoneId = dashboard.zone?.objectId || dashboard.zone;
                    return dashboardSiteId === siteId && dashboardZoneId === zoneId;
                });
            };
        },
        
        // Check if a site has any children (dashboards or zones)
        hasSiteChildren() {
            return (siteId) => {
                const siteDashboards = this.getSiteDashboards(siteId);
                const zones = this.getZonesForSite(siteId);
                return siteDashboards.length > 0 || zones.length > 0;
            };
        }
    },
    
    watch: {
        '$route'(to, from) {
            this.currentPath = to.path;
        },
        
        isVisible(newVal) {
            this.$emit('navigator-state-changed', {
                isCollapsed: this.isCollapsed,
                isVisible: newVal
            });
        }
    },
    
    methods: {
        togglePanel() {
            this.isCollapsed = !this.isCollapsed;
            this.saveCollapseState(this.isCollapsed);
            this.$emit('navigator-state-changed', {
                isCollapsed: this.isCollapsed,
                isVisible: this.isVisible
            });
        },
        
        toggleNode(nodeId) {
            this.$set(this.expanded, nodeId, !this.expanded[nodeId]);
        },
        
        async toggleSite(siteId) {
            const nodeId = `site-${siteId}`;
            this.$set(this.expanded, nodeId, !this.expanded[nodeId]);
            
            // Load zones when site is expanded
            if (this.expanded[nodeId] && !this.getZonesForSite(siteId).length) {
                await this.loadZonesForSite(siteId);
            }
        },
        
        toggleZone(zoneId) {
            const nodeId = `zone-${zoneId}`;
            this.$set(this.expanded, nodeId, !this.expanded[nodeId]);
        },
        
        async loadZonesForSite(siteId) {
            try {
                this.$set(this.loadingZones, siteId, true);
                await this.$store.dispatch('getZonesForSite', siteId);
            } catch (error) {
                console.error('Failed to load zones for site:', siteId, error);
            } finally {
                this.$set(this.loadingZones, siteId, false);
            }
        },
        
        getZonesForSite(siteId) {
            return this.$store.state.zones[siteId] || [];
        },
        
        navigateToSite(siteId) {
            const projectId = this.currentProject.objectId;
            if (!projectId) {
                console.error('Navigator: projectId not available for site navigation');
                return;
            }
            const targetPath = `/projects/${projectId}/sites/${siteId}`;
            if (this.$route.path !== targetPath) {
                this.$router.push({name: 'Site', params: {projectId: projectId, siteId}});
                window.location.reload();
                // this.$router.push(targetPath);
            }
        },
        
        navigateToZone(siteId, zoneId) {
            const projectId = this.currentProject.objectId;
            if (!projectId) {
                console.error('Navigator: projectId not available for zone navigation');
                return;
            }
            const targetPath = `/projects/${projectId}/sites/${siteId}/zones/${zoneId}`;
            if (this.$route.path !== targetPath) {
                this.$router.push({name: 'Zone', params: {projectId: projectId, siteId, zoneId}});
                window.location.reload();
                // this.$router.push(targetPath);
            }
        },
        
        navigateToDashboard(dashboardId) {
            const projectId = this.currentProject.objectId;
            if (!projectId) {
                console.error('Navigator: projectId not available for dashboard navigation');
                return;
            }
            const targetPath = `/projects/${projectId}/dashboards/${dashboardId}`;
            if (this.$route.path !== targetPath) {
                this.$router.push(targetPath);
            }
        },
        
        navigateToHome() {
            if (this.$route.path !== '/') {
                this.$router.push('/');
            }
        },
        
        navigateToDashboardsPage() {
            const projectId = this.currentProject.objectId;
            if (!projectId) {
                console.error('Navigator: projectId not available for dashboard navigation');
                return;
            }
            const targetPath = `/projects/${projectId}/dashboards`;
            if (this.$route.path !== targetPath) {
                this.$router.push(targetPath);
            }
        },
        
        isCurrentSite(siteId) {
            return this.$route.params.siteId === siteId.toString();
        },
        
        isCurrentZone(siteId, zoneId) {
            return this.$route.params.siteId === siteId.toString() && 
                   this.$route.params.zoneId === zoneId.toString();
        },
        
        isCurrentDashboard(dashboardId) {
            return this.$route.params.dashboardId === dashboardId.toString();
        },
        
        /**
         * Get the stored collapse state from local storage
         * @returns {boolean} The stored collapse state, defaults to false
         */
        getStoredCollapseState() {
            try {
                const stored = localStorage.getItem('navigator-collapsed');
                return stored === 'true';
            } catch (error) {
                console.warn('Failed to read navigator collapse state from local storage:', error);
                return false;
            }
        },
        
        /**
         * Save the collapse state to local storage
         * @param {boolean} isCollapsed - The collapse state to save
         */
        saveCollapseState(isCollapsed) {
            try {
                localStorage.setItem('navigator-collapsed', isCollapsed.toString());
            } catch (error) {
                console.warn('Failed to save navigator collapse state to local storage:', error);
            }
        },
        
        // Removed autoExpandToCurrentLocation() method
    },
    
    mounted() {
        try {
            this.currentPath = this.$route.path;
            // Do not load any data when the user is not authenticated
            if (!this.isLoggedIn) {
                return;
            }
            
            // Load sites if not already loaded
            if (this.sites.length === 0) {
                this.$store.dispatch('getSites');
            }
            
            // Load dashboards if not already loaded
            if (this.dashboards.length === 0 && this.currentProject.objectId) {
                this.$store.dispatch('getDashboards', this.currentProject.objectId);
            }
            
            // Navigator stays collapsed by default - no auto-expansion
            // Emit initial state
            this.$emit('navigator-state-changed', {
                isCollapsed: this.isCollapsed,
                isVisible: this.isVisible
            });
        } catch (error) {
            console.error('Navigator initialization error:', error);
        }
    }
}
</script>

<style lang="scss" scoped>
.navigator-side-panel {
    position: fixed;
    // Place panel below the top app bar (keep logo visible)
    top: var(--topbar-height, 64px);
    left: 0;
    height: calc(100vh - var(--topbar-height, 64px));
    width: 320px;
    background: var(--app-secondary-bg);
    border-right: 1px solid var(--app-tertiary-bg);
    box-shadow: var(--box-shadow);
    z-index: 1000;
    font-family: 'Roboto', sans-serif;
    transition: width 0.3s ease, transform 0.3s ease;
    display: flex;
    flex-direction: column;
    
    &.collapsed {
        width: 0px;
    }
    
    @media (max-width: 768px) {
        display: none;
    }
    
    @media (max-width: 1024px) {
        width: 280px;
        
        &.collapsed {
            width: 48px;
        }
    }
}

.navigator-toggle {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: -20px;
    width: 28px;
    height: 68px;
    background: var(--app-secondary-bg);
    border: 1px solid var(--app-tertiary-bg);
    border-left: none;
    border-radius: 0 12px 12px 0;
    box-shadow: none 0 0 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 1001;
    
    &:hover {
        background: var(--app-tertiary-bg);
    }
    
    .v-icon {
        color: var(--app-primary-txt);
        transition: transform 0.3s ease;
    }
}

.navigator-content {
    padding: 20px 16px;
    overflow-y: auto;
    flex: 1;
    // No extra top padding needed when toggle is vertically centered
    
    &::-webkit-scrollbar {
        width: 4px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
        background: var(--app-tertiary-txt);
        border-radius: 2px;
    }
}

.tree-node {
    display: flex;
    align-items: center;
    padding: 6px 0;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s ease;
    
    &:hover {
        background: var(--app-tertiary-bg);
    }
    
    &.project-node {
        font-weight: 600;
        color: var(--app-primary-txt);
        font-size: 14px;
    }
    
    &.section-node {
        font-weight: 500;
        color: var(--app-secondary-txt);
        font-size: 13px;
        margin-left: 16px;
    }
    
    &.site-node {
        margin-left: 32px;
        font-size: 12px;
    }
    
    &.site-dashboards-section {
        font-weight: 500;
        color: var(--app-secondary-txt);
        font-size: 13px;
        margin-left: 48px;
    }
    
    &.zone-section-node {
        font-weight: 500;
        color: var(--app-secondary-txt);
        font-size: 13px;
        margin-left: 48px;
    }
    
    &.zone-node {
        margin-left: 64px;
        font-size: 12px;
        
        &.loading {
            color: var(--app-tertiary-txt);
        }
    }
    
    &.dashboard-node {
        margin-left: 32px;
        font-size: 12px;
        
        &.site-dashboard {
            margin-left: 48px;
        }
        
        &.zone-dashboard {
            margin-left: 80px;
        }
    }

    .node-actions {
        margin-left: 8px;
        display: flex;
        align-items: center;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.15s ease;
        margin-left: auto;
    }
    &:hover .node-actions,
    &:focus-within .node-actions,
    &.expanded .node-actions {
        opacity: 1;
        visibility: visible;
    }
    .action-icon {
        color: var(--app-tertiary-txt);
        opacity: 0.85;
        font-size: 18px;
        &:hover { color: var(--app-primary-txt); opacity: 1; }
    }
}

.tree-caret {
    margin-right: 8px;
    transition: transform 0.2s ease;
    color: var(--app-tertiary-txt);
    
    &.expanded {
        transform: rotate(90deg);
    }
}

.left-action {
    width: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
    position: relative;
}
.left-spacer { width: 24px; display: inline-block; margin-right: 8px; }

.tree-folder {
    margin-right: 8px;
    color: var(--app-tertiary-txt);
    transition: color 0.2s ease;
    
    &.expanded {
        color: var(--app-primary-txt);
    }
}

.tree-spacer {
    width: 16px;
    // margin-right: 8px;
}

.tree-label {
    flex: 1;
    color: var(--app-secondary-txt);
    transition: color 0.2s ease;
    margin-left: 2px;
    width: fit-content;

    &.active { color: var(--app-primary-txt); font-weight: 600; }
}

.tree-children {
    overflow: hidden;
}

.loading-icon {
    margin-right: 8px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

// Expand/collapse transitions
.expand-enter-active,
.expand-leave-active {
    transition: all 0.3s ease;
    transform-origin: top;
}

.expand-enter,
.expand-leave-to {
    opacity: 0;
    transform: scaleY(0);
}

.expand-enter-to,
.expand-leave {
    opacity: 1;
    transform: scaleY(1);
}
</style>

<style lang="scss">
// Removed global margin shifts so the top navbar remains full-width
.v-main {
    transition: margin-left 0.3s ease;
}
</style>